import {Component, defineComponent, h, reactive, Reactive, watch} from "vue";
import {IActivityBusinessMeta, IActivityMetadata, IActivityTheme} from "@/model/activity";
import {UserAgent} from "@/model/user_agent";
import {Material} from "@/model/material";
import {Theme} from "@/views/editor/service/theme";
import {BlockOutlined, CommentOutlined, DatabaseOutlined, UserOutlined} from "@ant-design/icons-vue";
import {ItemType, RadioChangeEvent} from "ant-design-vue";
import {MenuInfo} from "ant-design-vue/es/menu/src/interface";
import {
    IBizAtmosphere,
    IBizChallenge,
    IBizCoupon,
    IBizProfitSharing,
    IBizPromotion,
    IBizRaffle,
    IBizRedEnvelope,
    IBizSign,
    IBusiness,
    INewBusiness,
} from "@/views/editor/service/business";
import atmosphere from "@/views/editor/service/atmosphere";
import promotion from "@/views/editor/service/promotion";
import challenge from "@/views/editor/service/challenge";
import raffle from "@/views/editor/service/raffle";
import coupon from "@/views/editor/service/coupon";
import profit_sharing from "@/views/editor/service/profit_sharing";
import sign from "@/views/editor/service/sign";
import red_envelope from "@/views/editor/service/red_envelope";
import {ISection} from "@/views/editor/service/types";
import {SubMenuType} from "ant-design-vue/es/menu/src/hooks/useItems";
import {api} from "@/lib/api";
import dayjs from "dayjs";
import {Notify} from "@/views/editor/service/notify";

const businessesClz: { [key: string]: { [key: string]: INewBusiness } } = {
    'atmosphere': atmosphere,
    'promotion': promotion,
    'challenge': challenge,
    'raffle': raffle,
    'coupon': coupon,
    'profit_sharing': profit_sharing,
    'red_envelope': red_envelope,
    'sign': sign,
};

export interface INewEditor {
    new(userAgent: UserAgent, material: Material, meta: IActivityMetadata): AEditor;
}

export abstract class AEditor {

    readonly meta: Reactive<IActivityMetadata>;
    readonly active = reactive({
        menu: {
            items: [
                {label: '活动页', key: 'page', icon: () => h(DatabaseOutlined)},
                {label: '弹窗', key: 'popups', icon: () => h(BlockOutlined), children: []},
                {
                    label: '成员', key: 'people', icon: () => h(UserOutlined), children: [
                        {label: '内部成员', key: 'internal'},
                        {label: '手机号顾客', key: 'member'}
                    ]
                },
                {label: '通知与跟进', key: 'notifies', icon: () => h(CommentOutlined)},
            ] as ItemType[],
            toggle: false,
            openKeys: ['popups'] as string[],
            selectedKeys: ['page'] as string[],
        },
        content: {layer: defineComponent({}) as Component, CTUIs: [] as Component[], DPUIs: [] as Component[]},
        preview: {branch_id: null as null | string, share_title: ''},
        status: {loading: false, lastSaveTS: ''},
    })

    readonly notify: Notify;

    // UI
    readonly theme: Theme;
    readonly section: { [key: string]: ISection } = {};

    // 模块
    readonly businesses: IBusiness[];
    abstract readonly customBusinessRange: string[]
    atmosphere: IBizAtmosphere | undefined
    promotion: IBizPromotion | undefined
    challenge: IBizChallenge | undefined
    raffle: IBizRaffle | undefined
    coupon: IBizCoupon | undefined
    profit_sharing: IBizProfitSharing | undefined
    red_envelope: IBizRedEnvelope | undefined
    sign: IBizSign | undefined


    protected constructor(public readonly userAgent: UserAgent, public readonly material: Material, meta: IActivityMetadata) {
        // 初始化数据
        this.meta = reactive(meta);
        this.initSectionUI()
        this.theme = new Theme(this);
        meta.activity_id || this.initBiz()
        this.businesses = this.meta.businesses.map(abm => meta.activity_id ? this.genBiz(abm) : this.genBiz(abm).resetMeta())

        this.notify = new Notify(this)

        // 初始化UI
        this.bizMetaToUX()
        this.initMenu();
        this.onMenuClick({
            domEvent: new MouseEvent(''),
            eventKey: "",
            eventKeyPath: [],
            item: {},
            key: '',
            keyPath: ['page']
        })
        // 做好监听
        watch(() => this.meta.ux, () => this.syncToMenu(), {deep: true})

    }

    protected abstract initSectionUI(): void

    /**
     * 根据自己的版本部署自己的菜单
     * @protected
     */
    protected abstract initMenu(): void

    protected syncToMenu() {
        console.log('changeToMenu:', this.active.menu.items)
        this.active.menu.items.forEach((item) => {
            if (item && item.key == 'popups') (item as SubMenuType).children =
                this.meta.ux.popups.map(p => ({label: p.name, key: p.id}))
        })
    }

    private bizMetaToUX() {
        const targets = this.bizPaths()
        this.meta.ux.page.action_bar = this.section['page.action.bar'].meta
        this.meta.ux.page.body = Object.keys(this.section).filter(k => k.startsWith('page.body'))
            .map(k => this.section[k].meta).filter(s => this.auth(s.dependencies, targets))
        this.meta.ux.page.status_bar = this.section['page.status.bar'].meta

        this.meta.ux.popups = Object.keys(this.section).filter(k => k.startsWith('popups.'))
            .map(k => this.section[k].meta).filter(s => this.auth(s.dependencies, targets))
    }

    /**
     * 新创建活动时初始化业务数据，并根据数据部署其它对象的相关业务
     * @protected
     */
    protected abstract initBiz(): void

    /**
     * 点击菜单时
     * @param info
     */
    abstract onMenuClick(info: MenuInfo): void;

    onBizChange(kind: string, evt: boolean): void {
        if (evt) {
            const targets = this.bizPaths()
            this.meta.ux.page.body.push(...Object.keys(this.section).filter(k => k.startsWith('page.body'))
                .map(k => this.section[k].meta).filter(s => s.dependencies.some(d => d.startsWith(kind)))
                .filter(s => this.auth(s.dependencies, targets)))
            this.meta.ux.popups.push(...Object.keys(this.section).filter(k => k.startsWith('popups'))
                .map(k => this.section[k].meta).filter(s => s.dependencies.some(d => d.startsWith(kind)))
                .filter(s => this.auth(s.dependencies, targets)))
        } else {
            this.meta.ux.page.body = this.meta.ux.page.body.filter(b => !b.dependencies.some(d => d.startsWith(kind)))
            this.meta.ux.popups = this.meta.ux.popups.filter(b => !b.dependencies.some(d => d.startsWith(kind)))
        }
        console.log('onBizChange:', this.meta.ux);
    }

    onImplementerChange(evt: RadioChangeEvent, meta: IActivityBusinessMeta): void {
        console.log('onImplementerChange:', evt.target.value, meta);
        const b = this.genBiz(meta);
        b.resetMeta()
        this.businesses[this.businesses.findIndex(b => b.meta.kind == meta.kind)] = b

        this.meta.ux.page.body = this.meta.ux.page.body.filter(b => !b.dependencies.some(d => d.startsWith(meta.kind)))
        this.meta.ux.popups = this.meta.ux.popups.filter(b => !b.dependencies.some(d => d.startsWith(meta.kind)))

        const targets = this.bizPaths()
        this.meta.ux.page.body.push(...Object.keys(this.section).filter(k => k.startsWith('page.body'))
            .map(k => this.section[k].meta).filter(s => s.dependencies.some(d => d.startsWith(meta.kind)))
            .filter(s => this.auth(s.dependencies, targets)))
        this.meta.ux.popups.push(...Object.keys(this.section).filter(k => k.startsWith('popups'))
            .map(k => this.section[k].meta).filter(s => s.dependencies.some(d => d.startsWith(meta.kind)))
            .filter(s => this.auth(s.dependencies, targets)))
        console.log('onImplementerChange:', this.meta.ux);
    }

    onBizSubChange(subs: string[], meta: IActivityBusinessMeta): void {
        console.log('onBizSubChange', meta, subs);
        const prefix = meta.kind + '/' + meta.implementer + '/'
        this.meta.ux.page.body = this.meta.ux.page.body.filter(b => !b.dependencies.some(d => d.startsWith(prefix)))
        this.meta.ux.popups = this.meta.ux.popups.filter(b => !b.dependencies.some(d => d.startsWith(prefix)))

        const targets = this.bizPaths()
        this.meta.ux.page.body.push(...Object.keys(this.section).filter(k => k.startsWith('page.body'))
            .map(k => this.section[k].meta).filter(s => s.dependencies.some(d => d.startsWith(prefix)))
            .filter(s => this.auth(s.dependencies, targets)))
        this.meta.ux.popups.push(...Object.keys(this.section).filter(k => k.startsWith('popups'))
            .map(k => this.section[k].meta).filter(s => s.dependencies.some(d => d.startsWith(prefix)))
            .filter(s => this.auth(s.dependencies, targets)))
        console.log('onBizSubChange:', JSON.parse(JSON.stringify(this.meta.ux)));
    }

    protected genBiz(meta: IActivityBusinessMeta): IBusiness {
        const b = new businessesClz[meta.kind][meta.implementer](this, meta);
        switch (meta.kind) {
            case 'atmosphere':
                this.atmosphere = b as IBizAtmosphere;
                return b
            case 'promotion':
                this.promotion = b as IBizPromotion;
                return b
            case 'challenge':
                this.challenge = b as IBizChallenge;
                return b
            case 'raffle':
                this.raffle = b as IBizRaffle;
                return b
            case 'coupon':
                this.coupon = b as IBizCoupon;
                return b
            case 'profit_sharing':
                this.profit_sharing = b as IBizProfitSharing;
                return b
            case 'red_envelope':
                this.red_envelope = b as IBizRedEnvelope
                return b
            case 'sign':
                this.sign = b as IBizSign;
                return b
            default:
                throw new Error(`Unknown meta: ${meta.kind}`)
        }
    }

    protected bizPaths(kind?: string): string[] {
        const arr: string[] = kind ? [] : ['activity', 'activity/theme', 'activity/ad']
        for (const b of this.meta.businesses) {
            if (b.enabled && (kind ? kind == b.kind : true)) {
                arr.push(b.kind + '/' + b.implementer)
                b.subs.forEach(sb => {
                    arr.push(b.kind + '/' + b.implementer + '/' + sb)
                })
            }
        }
        return arr
    }

    public auth(dependencies: string[], targets?: string[]): boolean {
        const ts = targets ?? this.bizPaths()
        return dependencies.every(dependency => ts.some(target => target.startsWith(dependency)))
    }

    // public async refresh(): Promise<void> {
    //     emit.emit('editor.refresh');
    //     this.meta.title_preview = await this.titlePreview(this.meta.title_template)
    // this.active.preview.share_title = await this.titlePreview(this.meta.share_title_template)
    // }

    async titleVariables(): Promise<string[]> {
        return api<void, string[]>({
            url: `user/${this.userAgent.meta.account.agent_id}/activity/title/variables`,
            method: 'post',
            data: {activity: this.meta, branch_id: this.active.preview.branch_id}
        });
    }

    async titlePreview(title: string): Promise<string> {
        return api<void, any>({
            url: `user/${this.userAgent.meta.account.agent_id}/activity/title`,
            method: 'POST',
            data: {activity: this.meta, branch_id: this.active.preview.branch_id, title_template: title},
        }).then(res => res.title)
    }

    public async save(): Promise<void> {
        //
    }

    public async publish(): Promise<void> {
        //
    }
}

export const GenActivityMeta = async (ua: UserAgent,material: Material, tags: string[], theme_id: string | null): Promise<IActivityMetadata> => {
    const meta: IActivityMetadata = {
        activity_id: "",
        ad: {
            custom: {
                contact_name: "",
                contact_phone_number: "",
                contact_wechat_qr_string: "",
                entrance_prompt: "我也要免费创建拓客活动",
                popup_prompt: "专业营销获客找管店宝"
            }, status: 'system'
        },
        agent_id: "",
        begin_at: new Date().toISOString(),
        branch_ids: [],
        businesses: [],
        create_at: new Date().toISOString(),
        end_at: dayjs().add(7, 'day').toDate().toISOString(),
        image_preview_url: "",
        publish_at: null,
        share_title_template: material.titles.filter(tags)?.first() + '[客服：<成员名>]' ?? '',
        tags: tags,
        theme: {
            card: Object.values(material.cards.data).filter(c => c.internal_name.includes('公共'))[0],
            "images": [],
            "keys": [0, 0, 0, 0],
            "theme_id": theme_id ?? null
        },
        title_preview: '',
        title_template: material.titles.filter(tags)?.first() ?? '',
        trash_at: null,
        ux: {
            page: {
                action_bar: {id: '', dependencies: [], name: '', can_hide: false, is_hide: false},
                body: [],
                status_bar: {id: '', dependencies: [], name: '', can_hide: false, is_hide: false}
            },
            popups: [],
            notifies: [],
        },
        wx_merchant_ids: [],
        employees: {},
    }
    meta.theme = await api<void, IActivityTheme>({url: `user/${ua.meta.account.agent_id}/activity/theme/meta`, params: {theme_id}})
    return meta
}